import clsx from "clsx";

import type { Campaign } from "../../api/generated";

import { BaseAlertTriangleIcon, BaseArrowDownIcon } from "../../Icons";
import { Typo } from "../../Typo";

export type CampaignPickerTriggerProps = {
  id: string;
  label?: string;
  subLabel?: string;
  value?: Campaign | null;
  placeholder: string;
  open: boolean;
  disabled?: boolean;
  error?: string;
  errorId?: string;
  onActivate: () => void;
  onArrowDown?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
};

export const CampaignPickerTrigger = ({
  id,
  label,
  subLabel,
  value,
  placeholder,
  open,
  disabled = false,
  error,
  errorId,
  onActivate,
  onArrowDown,
  inputRef,
}: CampaignPickerTriggerProps) => {
  return (
    <>
      {label && (
        <label htmlFor={id} className="flex flex-col gap-1">
          <Typo element="p" type="labelSM" smType="labelSM">
            {label}
          </Typo>
          {subLabel && (
            <Typo element="p" type="bodyXS" textColor="text-text-secondary">
              {subLabel}
            </Typo>
          )}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          readOnly
          disabled={disabled}
          value={value ? value.name : placeholder}
          placeholder={placeholder}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onClick={() => {
            if (disabled) return;
            onActivate();
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onActivate();
            } else if (event.key === "ArrowDown" && !open) {
              event.preventDefault();
              onArrowDown?.();
            }
          }}
          className={clsx(
            "typo-body-sm h-10 w-full rounded-md p-2 pr-8 focus:outline-none",
            "placeholder:typo-body-sm placeholder-text-tertiary",
            disabled
              ? "cursor-not-allowed bg-surface-disabled text-text-disabled"
              : clsx(
                  "cursor-pointer bg-surface-tonal-default text-text-primary",
                  "hover:ring-2 hover:ring-stroke-focused focus:ring-2 focus:ring-stroke-focused",
                ),
          )}
        />
        <div className="pointer-events-none absolute inset-y-0 end-3 flex items-center">
          <BaseArrowDownIcon
            className={clsx(
              "h-4 w-4 text-icon-secondary transition-transform",
              open && "rotate-180",
            )}
          />
        </div>
      </div>

      {error && !open && (
        <div id={errorId} role="alert" className="flex gap-1">
          <div className="flex items-center py-px text-icon-error">
            <BaseAlertTriangleIcon className="h-4 w-4" />
          </div>
          <Typo element="p" type="bodyXS" textColor="text-text-error">
            {error}
          </Typo>
        </div>
      )}
    </>
  );
};
